import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { useClientProject } from '@/contexts'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { SemanticDashboard } from '@/components/charts/SemanticDashboard'
import { DashboardGridSkeleton } from '@/components/route-shell/skeletons/DashboardGridSkeleton'
import { getCustomDashboard, getDashboardSuggestions, listCustomDashboards } from '@/services/dashboardService'
import { chatbotService } from '@/services'
import type { ChatMessage } from '@/types'
import { isDashboardDefinition } from '@chart-core'
import type { DashboardInput } from '@chart-core'
import { DashboardPromptHero } from './-custom-dashboards/DashboardPromptHero'
import { DashboardGenerationView } from './-custom-dashboards/DashboardGenerationView'
import type { GenerationTerminal } from './-custom-dashboards/DashboardGenerationView'
import { DashboardRefineBar } from './-custom-dashboards/DashboardRefineBar'
import '@/styles/pages.css'
import '@/styles/custom-dashboards.css'

interface CustomDashboardsSearch {
  /** id of the saved dashboard to render (short or dashed form); omitted = nothing selected yet */
  dashboard?: string
  /** active section tab within the rendered dashboard (mirrors overview's ?tab=) */
  tab?: string
}

// DB-backed dashboards (custom_dashboard rows, served by GET /api/projects/{uuid}/dashboards).
// The page is prompt-first: with nothing selected it shows the "What do you want to see?" hero
// (composer + suggested prompts), generating streams the dashboard-builder agent's SSE progress
// into a status card over a skeleton grid, and a rendered dashboard carries a floating refine bar
// that feeds the same agent pinned to the dashboard on screen.
//   /organization/custom-dashboards?projectId=…[&dashboard=<id>]
export const Route = createFileRoute('/_app/organization/custom-dashboards')({
  head: () => ({ meta: [{ title: 'Custom Dashboards · AERRIS' }] }),
  component: CustomDashboardsPage,
  validateSearch: (search: Record<string, unknown>): CustomDashboardsSearch => ({
    dashboard: typeof search.dashboard === 'string' ? search.dashboard : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
})

/** The definition is schema-validated on write, but guard the render against a junk row anyway. */
const isRenderable = (definition: unknown): definition is DashboardInput =>
  Array.isArray(definition) || isDashboardDefinition(definition as DashboardInput)

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  color: 'var(--text-muted, #6b7280)',
  textAlign: 'center',
  backgroundColor: 'var(--container-bg, #ffffff)',
  border: '1px solid var(--border-color, #e5e7eb)',
  borderRadius: 'var(--radius-lg)',
  padding: '2rem',
}

function Panel({ title, body }: { title: string; body?: string }) {
  return (
    <div style={panelStyle}>
      <h2 style={{ fontSize: '1.125rem', margin: 0 }}>{title}</h2>
      {body && <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>{body}</p>}
    </div>
  )
}

const navStyle: CSSProperties = {
  width: '260px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '1rem',
  borderRight: '1px solid var(--border-color, #e5e7eb)',
  backgroundColor: 'var(--container-bg, #ffffff)',
  overflowY: 'auto',
}

const createButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.6rem 0.75rem',
  cursor: 'pointer',
  backgroundColor: 'var(--accent-ink, #16324f)',
  color: '#ffffff',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 600,
}

const navItemStyle = (active: boolean): CSSProperties => ({
  width: '100%',
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  cursor: 'pointer',
  backgroundColor: active ? 'var(--surface-secondary, #f3f4f6)' : 'transparent',
  border: `1px solid ${active ? 'var(--border-color, #e5e7eb)' : 'transparent'}`,
  borderRadius: 'var(--radius-md)',
})

/** One in-flight agent run: what was asked, what the agent has said so far, how it ended. */
interface GenerationState {
  prompt: string
  /** Progress texts in arrival order — seeded synthetically, then the SSE THINKING messages. */
  steps: string[]
  /** Name of the dashboard being built: known up front on a refine, learned on the first save. */
  title: string | null
  /** Set when the run ended without a dashboard (failure, or the agent replied without saving). */
  terminal: GenerationTerminal | null
}

function CustomDashboardsPage() {
  const { projectId, selectedProject } = useClientProject()
  const { dashboard: dashboardParam, tab } = Route.useSearch()
  // The param arrives in either id form (short from new links, dashed from old ones); the
  // API calls below need the UUID.
  const dashboardUuid = normalizeEntityId(dashboardParam)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [generation, setGeneration] = useState<GenerationState | null>(null)
  // Dashboard last (re)generated on this page, to caption it "Generated from your prompt".
  const [lastGeneratedUuid, setLastGeneratedUuid] = useState<string | null>(null)
  // Bumped after every generation/refresh: remounts SemanticDashboard so its charts refetch.
  const [refreshKey, setRefreshKey] = useState(0)

  const closeConnectionRef = useRef<(() => void) | null>(null)
  // The dashboard the CURRENT run is building. Seeded from the refine target (undefined for a
  // fresh build) and overwritten by mid-stream DASHBOARD parts, so a retry — or a second save in
  // the same turn — updates the dashboard the agent already created instead of duplicating it.
  const targetDashboardRef = useRef<string | undefined>(undefined)
  // One agent conversation per dashboard being worked on: consecutive refines of the same
  // dashboard share a session (the agent keeps its context); switching targets starts fresh.
  const sessionRef = useRef<{ uuid: string; target: string | undefined } | null>(null)

  useEffect(() => {
    return () => closeConnectionRef.current?.()
  }, [])

  const list = useQuery({
    queryKey: ['custom-dashboards', projectId],
    queryFn: () => listCustomDashboards(projectId!),
    enabled: !!projectId,
  })
  const selected = useQuery({
    queryKey: ['custom-dashboard', projectId, dashboardUuid],
    queryFn: () => getCustomDashboard(projectId!, dashboardUuid!),
    enabled: !!projectId && !!dashboardUuid,
  })
  // Server-side this is an LLM run on a cold cache, so it can take a while: the hero renders
  // shimmer chips until it resolves, and an error (e.g. an older server) just hides the row.
  const suggestions = useQuery({
    queryKey: ['dashboard-suggestions', projectId],
    queryFn: () => getDashboardSuggestions(projectId!),
    enabled: !!projectId && !dashboardUuid && !generation,
    staleTime: Infinity,
    retry: false,
  })

  const stopGeneration = () => {
    closeConnectionRef.current?.()
    closeConnectionRef.current = null
    setGeneration(null)
  }

  const handleMessage = (message: ChatMessage) => {
    if (message.type === 'THINKING') {
      const text = message.message
      if (!text) return
      setGeneration((prev) =>
        prev && prev.steps[prev.steps.length - 1] !== text
          ? { ...prev, steps: [...prev.steps, text] }
          : prev,
      )
    } else if (message.type === 'DASHBOARD' && message.dashboard) {
      const { uuid, name } = message.dashboard
      targetDashboardRef.current = uuid
      if (sessionRef.current) sessionRef.current.target = uuid
      setGeneration((prev) => (prev ? { ...prev, title: name || prev.title } : prev))
      // The saved definition changed server-side; drop cached copies so the render is fresh.
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard', projectId, uuid] })
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards', projectId] })
    } else if (message.type === 'ANSWER') {
      const finished = targetDashboardRef.current
      closeConnectionRef.current = null
      if (finished) {
        setGeneration(null)
        setLastGeneratedUuid(finished)
        setRefreshKey((k) => k + 1)
        navigate({
          to: '/organization/custom-dashboards',
          search: (prev) => ({ ...prev, dashboard: toShortEntityId(finished), tab: undefined }),
          replace: true,
        })
      } else {
        // The agent finished without saving anything — usually a clarifying question. Show its
        // reply in the status card rather than silently bouncing back to the hero.
        const text =
          message.message || 'The dashboard was not generated. Try rephrasing your prompt.'
        setGeneration((prev) => (prev ? { ...prev, terminal: { kind: 'notice', text } } : prev))
      }
    } else if (message.type === 'QUESTION' && message.question) {
      // The agent asked a clarifying question and ended its turn (the stream is closed): show
      // the question with its option chips; the answer continues the same conversation.
      const { question, options } = message.question
      closeConnectionRef.current = null
      setGeneration((prev) =>
        prev ? { ...prev, terminal: { kind: 'question', text: question, options } } : prev,
      )
    } else if (message.type === 'ERROR') {
      closeConnectionRef.current = null
      setGeneration((prev) =>
        prev
          ? { ...prev, terminal: { kind: 'error', text: message.message ?? 'Something went wrong.' } }
          : prev,
      )
    }
  }

  /** Kick off an agent run: `target` = the dashboard to refine, undefined to build a new one. */
  const startGeneration = (prompt: string, target: string | undefined, title: string | null) => {
    if (!projectId) return
    closeConnectionRef.current?.()
    targetDashboardRef.current = target
    if (!sessionRef.current || sessionRef.current.target !== target) {
      sessionRef.current = { uuid: crypto.randomUUID(), target }
    }
    setGeneration({ prompt, steps: ['Reading your prompt'], title, terminal: null })
    closeConnectionRef.current = chatbotService.sendDashboardMessage(
      {
        message: prompt,
        chatSessionUuid: sessionRef.current.uuid,
        projectUuid: projectId,
        dashboardUuid: target,
      },
      handleMessage,
    )
  }

  const generateNew = (prompt: string) => startGeneration(prompt, undefined, null)
  const refine = (prompt: string) =>
    startGeneration(prompt, dashboardUuid, selected.data?.name ?? null)
  const retry = () => {
    if (!generation) return
    startGeneration(generation.prompt, targetDashboardRef.current, generation.title)
  }
  /** Answer a clarifying question (or reply to a notice): same session and target, steps kept. */
  const replyToAgent = (text: string) => {
    if (!projectId || !sessionRef.current) return
    closeConnectionRef.current?.()
    setGeneration((prev) =>
      prev ? { ...prev, steps: [...prev.steps, `You: ${text}`], terminal: null } : prev,
    )
    closeConnectionRef.current = chatbotService.sendDashboardMessage(
      {
        message: text,
        chatSessionUuid: sessionRef.current.uuid,
        projectUuid: projectId,
        dashboardUuid: targetDashboardRef.current,
      },
      handleMessage,
    )
  }

  const open = (uuid: string | undefined) => {
    stopGeneration()
    navigate({
      to: '/organization/custom-dashboards',
      // Preserve the project selection (kept in URL search by ClientProjectContext); reset the
      // dashboard-specific tab when switching dashboards.
      search: (prev) => ({ ...prev, dashboard: uuid ? toShortEntityId(uuid) : undefined, tab: undefined }),
    })
  }
  const setTab = (next: string) =>
    navigate({
      to: '/organization/custom-dashboards',
      search: (prev) => ({ ...prev, tab: next }),
    })
  // Back to the prompt hero: the empty state IS the create flow.
  const createNew = () => open(undefined)
  const editInBuilder = () =>
    navigate({
      to: '/organization/custom-dashboards/new',
      search: (prev) => ({
        ...prev,
        dashboard: dashboardUuid ? toShortEntityId(dashboardUuid) : undefined,
        tab: undefined,
      }),
    })
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-dashboard', projectId, dashboardUuid] })
    setRefreshKey((k) => k + 1)
  }

  if (!projectId) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Panel title="Select a Client and Project" body="Choose a client and project to view its saved dashboards" />
      </div>
    )
  }

  const generationTitle = generation?.title ?? 'New dashboard'
  const showRefineBar =
    !generation && !!dashboardUuid && !!selected.data && isRenderable(selected.data.definition)

  return (
    <div className="custom-dash-page">
      <aside style={navStyle}>
        <button type="button" onClick={createNew} style={createButtonStyle}>
          <Plus size={16} />
          Create Dashboard
        </button>
        {list.isPending ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>Loading dashboards…</span>
        ) : list.isError ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>Couldn't load dashboards</span>
        ) : list.data.length === 0 ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>
            No saved dashboards yet
          </span>
        ) : (
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {list.data.map((dashboard) => (
              <button
                key={dashboard.uuid}
                type="button"
                onClick={() => open(dashboard.uuid)}
                style={navItemStyle(dashboard.uuid === dashboardUuid)}
              >
                <span style={{ fontWeight: 600, display: 'block' }}>{dashboard.name ?? 'Untitled dashboard'}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)' }}>
                  updated {new Date(dashboard.updatedAt).toLocaleDateString()}
                  {typeof dashboard.widgetCount === 'number' &&
                    ` · ${dashboard.widgetCount} widget${dashboard.widgetCount === 1 ? '' : 's'}`}
                </span>
              </button>
            ))}
          </nav>
        )}
      </aside>
      <main className={showRefineBar ? 'custom-dash-main custom-dash-main--with-bar' : 'custom-dash-main'}>
        <div className="custom-dash-scroll">
          {generation ? (
            <>
              <header className="custom-dash-header">
                <div>
                  <h1>{generationTitle}</h1>
                  <p className="custom-dash-header__meta">Building from: “{generation.prompt}”</p>
                </div>
              </header>
              <DashboardGenerationView
                steps={generation.steps}
                terminal={generation.terminal}
                onRetry={retry}
                onCancel={stopGeneration}
                onReply={replyToAgent}
              />
            </>
          ) : !dashboardUuid ? (
            <DashboardPromptHero
              contractCount={selectedProject?.contractCount}
              suggestions={suggestions.data ?? (suggestions.isError ? [] : undefined)}
              onGenerate={generateNew}
            />
          ) : selected.isPending ? (
            <DashboardGridSkeleton label="dashboard" />
          ) : selected.isError ? (
            <Panel title="Couldn't load this dashboard" body={String(selected.error)} />
          ) : !selected.data ? (
            <Panel title="Dashboard not found" body="It may have been deleted, or it belongs to another project." />
          ) : !isRenderable(selected.data.definition) ? (
            <Panel title="This dashboard can't be rendered" body="Its saved definition isn't a recognised dashboard shape." />
          ) : (
            <>
              <header className="custom-dash-header">
                <div>
                  <h1>{selected.data.name ?? 'Untitled dashboard'}</h1>
                  <p className="custom-dash-header__meta">
                    {dashboardUuid === lastGeneratedUuid && 'Generated from your prompt · '}
                    updated {new Date(selected.data.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="custom-dash-header__actions">
                  <button type="button" onClick={refresh}>
                    <RefreshCw size={14} aria-hidden="true" />
                    Refresh
                  </button>
                  <button type="button" onClick={editInBuilder}>Edit in builder</button>
                </div>
              </header>
              <SemanticDashboard
                key={refreshKey}
                projectUuid={projectId}
                definitions={selected.data.definition}
                tab={tab}
                onTabChange={setTab}
              />
            </>
          )}
        </div>
        {showRefineBar && <DashboardRefineBar onSend={refine} />}
      </main>
    </div>
  )
}
